import { useContext, useEffect, useLayoutEffect, useCallback } from 'react';
import { GameLoopContext } from '@/shared/GameComponents/GameLoopContext/GameLoopContext';
import { GameStageContext } from '@/shared/GameComponents/GameStageContext/GameStageContext';
import { shuffleList } from '@/modules/gameUtils/random';
import { AudioAutoPlayerContext } from '@/shared/Players/AudioAutoPlayer/AudioAutoPlayerContext';
import { GameAssetsContext } from '@/shared/GameComponents/GameAssetsContext/GameAssetsContext';
import debounce from 'lodash/debounce';
import { FullscreenContext } from '@/shared/FullscreenContext/FullscreenContext';
import { useExpandedScreenContext } from '@/shared/ThemeDetail/ExpandedScreen/ExpandedScreenContext';
import { getDragAndMatchLayout } from './getDrangAndMatchLayout';

export default function useDragAndMatchGameLoop({ onAnswer, onFinish }) {
  const game = useContext(GameLoopContext);
  const gameStage = useContext(GameStageContext);
  const audioPlayer = useContext(AudioAutoPlayerContext);
  const { state: assets } = useContext(GameAssetsContext);

  const { state: fullScreenContextState } = useContext(FullscreenContext);
  const { state: expandedContextScreenState } = useExpandedScreenContext();

  const actions = {
    onClick: async (txt) => {
      if (game.state.finished) {
        await audioPlayer.actions.play(assets.sounds[txt.id].audio);
      }
    },

    onDragStart: async ({ active }) => {
      await audioPlayer.actions.play(assets.sounds[active.id].audio);
    },
    onDragOver: ({ over }) => {
      game.actions.setState({
        currentOverId: over?.id,
      });
    },
    onDragEnd: ({ active, over }) => {
      if (over) {
        const answer = {
          id: active.id,
          answer: game.state.gameConf.txts.find((txt) => txt.id == active.id)
            .text,
          correct: active.id == over.id,
          rigthAnswer: [
            game.state.gameConf.txts.find((txt) => txt.id == over.id).text,
          ],
          answerId: over.id,
        };

        const answersLog = [...game.state.answersLog, answer];
        let finished = answersLog.length == game.state.gameConf.txts.length;
        game.actions.setState({
          lastCorrectId: answer.correct ? answer.id : null,
          answersLog,
          finished,
          currentOverId: null,
        });

        onAnswer({ gameConf: game.state.gameConf, answers: answersLog });
        if (finished) {
          onFinish(answersLog);
        }
      } else {
        game.actions.setState({
          currentOverId: null,
        });
      }
    },
  };

  // Initialize game
  useEffect(() => {
    if (game.state.gameConf.layout == 'fixed') {
      const imgs = game.state.gameConf.imgs;

      const transforms = shuffleList(
        game.state.gameConf.txts.map((txt) => txt.transform)
      );

      const txts = game.state.gameConf.txts.map((txt) => {
        const img = imgs.find((img) => img.id == txt.id);
        return {
          ...txt,
          transform: transforms.pop(),
          specialFontSize: game.state.gameConf.fontSize,
          correctTransform: {
            ...txt.transform,
            x: img.transform.x,
            y: img.transform.y,
          },
        };
      });

      game.actions.setState({
        txts,
        imgs,
        answersLog: [],
        lastCorrectId: null,
      });
    } else {
      const { randomTxts, randomImgs } = setRandomOrder(game.state.gameConf);
      game.actions.setState({
        randomTxts,
        randomImgs,
        txts: [],
        imgs: [],
        answersLog: [],
        lastCorrectId: null,
      });
    }
  }, []);

  const updateLayout = useCallback(
    (width, height) => {
      if (!game.state.randomImgs) return;

      const aspectRatio = width / height;
      const [images, txts] = getDragAndMatchLayout(
        game.state.randomImgs,
        game.state.randomTxts,
        aspectRatio,
        width
      );

      game.actions.setState({
        txts,
        imgs: images,
      });
    },
    [game.state.randomImgs, game.state.randomTxts]
  );

  const debouncedUpdateLayout = useCallback(
    debounce((width, height) => {
      updateLayout(width, height);
    }, 150),
    [updateLayout]
  );

  useLayoutEffect(() => {
    debouncedUpdateLayout(gameStage.state.width, gameStage.state.height);

    return () => {
      debouncedUpdateLayout.cancel();
    };
  }, [gameStage.state.width, gameStage.state.height, debouncedUpdateLayout]);

  useLayoutEffect(() => {
    debouncedUpdateLayout.cancel();
    updateLayout(gameStage.state.width, gameStage.state.height);
  }, [
    fullScreenContextState?.isFullscreen,
    expandedContextScreenState?.isExpandedscreen,
    updateLayout,
    gameStage.state.width,
    gameStage.state.height,
  ]);

  return { state: game.state, actions: { ...actions, ...game.actions } };
}

function setRandomOrder({ txts, imgs }) {
  const randomTxts = shuffleList(txts).map((txt) => ({
    id: txt.id,
    text: txt.text,
    icon: txt.icon,
    transform: txt.transform,
  }));

  const randomImgs = shuffleList(imgs).map((img) => ({
    id: img.id,
    atlasImg: img.atlasImg,
    transform: img.transform,
  }));

  return { randomTxts, randomImgs };
}
